import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Loader2, Send, Bug } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { formatDebugContext } from "@/lib/debugCapture";
import { getSessionId } from "@/lib/sessionId";

type Category = "bug" | "feature" | "general" | "other";

const CATEGORIES: { id: Category; label: string }[] = [
  { id: "bug", label: "Bug" },
  { id: "feature", label: "Feature" },
  { id: "general", label: "General" },
  { id: "other", label: "Other" },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Compact in-page bug-report sheet. Mirrors the /feedback form but keeps the
 * user on the page they were debugging (like the Tutor FAB) so they don't
 * lose context. The full /feedback page still exists for deeper reports.
 */
export default function ReportBugSheet({ open, onOpenChange }: Props) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [category, setCategory] = useState<Category>("bug");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  // Auto-append debug context whenever the sheet opens fresh.
  useEffect(() => {
    if (!open) return;
    setSent(false);
    if (!message) {
      setMessage(
        [
          `What happened:`,
          ``,
          `(describe what you were doing)`,
          ``,
          `--- Debug context (auto-captured) ---`,
          formatDebugContext(),
        ].join("\n"),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    if (subject.trim().length < 1 || message.trim().length < 10) {
      toast.error("Add a short subject and at least 10 characters of detail.");
      return;
    }
    setLoading(true);
    const currentUrl =
      typeof window !== "undefined" ? window.location.href.slice(0, 500) : null;
    const { error } = await supabase.from("feedback").insert({
      user_id: user?.id ?? null,
      email: email ? email : user?.email ?? null,
      category,
      subject: subject.slice(0, 200),
      message: message.slice(0, 5000),
      user_agent:
        typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
      page_url: currentUrl,
      session_id: getSessionId() || null,
      attachments: [],
    });
    setLoading(false);
    if (error) {
      console.error("feedback insert failed", error);
      toast.error("Could not send. Try again.");
      return;
    }
    setSent(true);
    setSubject("");
    setMessage("");
    setEmail("");
    toast.success("Thanks — feedback received!");
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader className="space-y-1.5">
          <SheetTitle className="flex items-center gap-2 font-mono">
            <Bug className="w-4 h-4 text-primary" />
            Report a bug
          </SheetTitle>
          <SheetDescription className="font-mono text-xs">
            Stays on this page. From <span className="text-foreground">{pathname}</span>
          </SheetDescription>
        </SheetHeader>

        {sent ? (
          <div className="mt-6 rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-2">
            <p className="font-mono text-sm text-foreground">Got it — thanks.</p>
            <button
              type="button"
              onClick={() => setSent(false)}
              className="text-xs font-mono text-primary hover:text-primary/80"
            >
              ← Send another
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-2">
              <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                Category
              </label>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCategory(c.id)}
                    className={`text-xs font-mono px-2.5 py-1 rounded-md border transition-colors ${
                      category === c.id
                        ? "border-primary/50 bg-primary/10 text-primary"
                        : "border-border bg-secondary text-foreground hover:border-primary/40"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="rbs-subject" className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                Subject
              </label>
              <Input
                id="rbs-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={200}
                disabled={loading}
                placeholder="Short summary"
                className="font-mono text-sm"
                required
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="rbs-message" className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                Message
              </label>
              <Textarea
                id="rbs-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={5000}
                disabled={loading}
                className="font-mono text-xs min-h-[180px] resize-y"
                required
              />
            </div>

            {!user && (
              <div className="space-y-2">
                <label htmlFor="rbs-email" className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                  Email <span className="text-muted-foreground/60">(optional)</span>
                </label>
                <Input
                  id="rbs-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  maxLength={320}
                  disabled={loading}
                  placeholder="you@example.com"
                  className="font-mono text-sm"
                />
              </div>
            )}

            <div className="flex items-center justify-between pt-1">
              <a
                href="/feedback?category=bug"
                className="text-[10px] font-mono text-muted-foreground hover:text-primary"
              >
                Need attachments? Open full form →
              </a>
              <button
                type="submit"
                disabled={loading || !subject.trim() || !message.trim()}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-primary text-primary-foreground font-mono text-xs font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send className="w-3.5 h-3.5" />
                    Send
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </SheetContent>
    </Sheet>
  );
}